// MessagePanel.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import LogoLoading from '../component/LogoLoading.jsx';
import MessageProgressBar from '../component/MessageProgressBar.jsx';
import StoryMultiProgressBar from '../component/StoryMultiProgressBar.jsx';
import Tooltip from '../component/Tooltip.jsx';
import { TooltipStyle as TooltipStyleComponent } from '../component/ShareButton.jsx';

import WithEditMessage from '../container/WithEditMessage.js';
import WithHideMessage from '../container/WithHideMessage.js';
import ShareButton from '../container/ShareButton.js';
import MuteButton from '../container/MuteButton.js';
import MessageThumbnail from '../container/MessageThumbnail.js';
import MessagePanelTop from '../container/MessagePanelTop.js';
import MessagePanelBottom from '../container/MessagePanelBottom.js';
import NewPostNotifier from '../container/NewPostNotifier.js';
import BroadcastList from '../container/BroadcastList.js';

import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import {
  color,
  iPhoneXSeriesFooterPaddingInPWA,
  footerNavigationHeight,
} from '../style/variables.js';
import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';

import { ShareType } from '../hook/useShortShareLink.js';

import ActiveLikeIcon from '../../img/icon-like-fill-shadow.svg';
import LikeIcon from '../../img/icon-like-outline-shadow.svg';
import ActiveDislikeIcon from '../../img/icon-unlike-fill-shadow.svg';
import DislikeIcon from '../../img/icon-unlike-outline-shadow.svg';

export class MessagePanel extends React.PureComponent {
  state = { isServer: true };
  nextTick = null;

  handleRatingClick = data => {
    const { submitRating } = this.props;
    submitRating(data);
  };

  componentDidMount() {
    this.initialize();
    this.nextTick = setTimeout(() => this.setState({ isServer: false }), 0);
  }

  componentDidUpdate(prevProps) {
    const {
      id,
      userId,
      isAuthed,
      fetchMeRating,
      fetchDetail,
      fetchViewScope,
      fetchUser,
    } = this.props;

    if (id !== prevProps.id) {
      if (id) {
        this.fetchData(id);
      }
    }

    if (userId !== prevProps.userId) {
      if (!userId) {
        this.fetchData(id);
      }
    }

    if (isAuthed !== prevProps.isAuthed) {
      // Update the view scope of the media
      if (id) {
        fetchDetail(id);
        fetchViewScope(id);

        if (isAuthed) {
          fetchMeRating(id);
        }
      }

      // Update the follow state
      const { userId } = this.props;
      if (userId) {
        fetchUser(userId);
      }
    }
  }

  componentWillUnmount() {
    const { id, setExpanded } = this.props;
    setExpanded({ expandedField: null, messageId: id });
    clearTimeout(this.nextTick);
  }

  render() {
    const { isServer } = this.state;
    const {
      id,
      userId,
      username,
      mediaType,
      ratedAs,
      duration,
      currentTime,
      expandedField,
      isInputFocused,
      hasAgeVerificationPinnedNotification,
      isPublished,
      isUnlocked,
      isUserActive,
      showThumbnail,
      showProgress,
      showInput,
      showOperationsBar,
      showLoader,
      showMultiProgress,
      totalCount,
      currentIndex,
      shareButtonTemplates,
      t,
      isActive,
    } = this.props;
    const isVideo = 0 === mediaType.search('video');
    const isCaptionExpanded = expandedField === 'caption';
    const isBroadcastListExpanded = expandedField === 'broadcastList';
    const showNotifier = this.getShouldShowNotifier();
    return (
      <StyledMessagePanel>
        <ThumbnailWrapper shouldShowPoster={showThumbnail}>
          <MessageThumbnail
            id={id}
            fetchPriority={isActive ? 'high' : 'auto'}
          />
        </ThumbnailWrapper>
        <PanelCover isExpanded={isCaptionExpanded}>
          {showProgress && isVideo && !isServer && (
            <ProgressWrapper>
              <MessageProgressBar max={duration} value={currentTime} />
            </ProgressWrapper>
          )}
          {showMultiProgress && (
            <StoryMultiProgressBar
              totalCount={totalCount || 1}
              currentIndex={currentIndex}
              currentDuration={duration}
              currentTime={currentTime}
            ></StoryMultiProgressBar>
          )}
          <TopWrapper shrinkHeight={this.getStrunkHeight()}>
            <MessagePanelTopWrapper>
              {userId && (
                <MessagePanelTop
                  id={id}
                  showUser={isUserActive}
                  isExpanded={isCaptionExpanded}
                  onExpand={status =>
                    this.handleExpand(status ? 'caption' : false)
                  }
                />
              )}
            </MessagePanelTopWrapper>
            {!isBroadcastListExpanded && (
              <ControlButtonsWrapper>
                {isPublished && (
                  <>
                    <Tooltip
                      position="left"
                      renderHint={() => (
                        <TooltipText>
                          {t('general_like', {
                            ns: TranslationNamespace.POST,
                          })}
                        </TooltipText>
                      )}
                      trigger="hover"
                    >
                      <ActionButton
                        isActive={ratedAs === 100}
                        data-element_id={ButtonId.All.ButtonLike}
                        data-tracking_payload={{
                          messageId: id,
                        }}
                        onClick={() =>
                          this.handleRatingClick({ id, rating: 100 })
                        }
                      >
                        {ratedAs === 100 ? (
                          <img
                            src={ActiveLikeIcon}
                            alt="active like"
                            width={32}
                            height={32}
                          />
                        ) : (
                          <img
                            src={LikeIcon}
                            alt="like"
                            width={32}
                            height={32}
                          />
                        )}
                      </ActionButton>
                    </Tooltip>
                    <Tooltip
                      position="left"
                      renderHint={() => (
                        <TooltipText>
                          {t('general_dislike', {
                            ns: TranslationNamespace.POST,
                          })}
                        </TooltipText>
                      )}
                      trigger="hover"
                    >
                      <ActionButton
                        isActive={ratedAs === 0}
                        data-element_id={ButtonId.All.ButtonDislike}
                        data-tracking_payload={{
                          messageId: id,
                        }}
                        onClick={() =>
                          this.handleRatingClick({ id, rating: 0 })
                        }
                      >
                        {ratedAs === 0 ? (
                          <img
                            src={ActiveDislikeIcon}
                            alt="active dislike"
                            width={32}
                            height={32}
                          />
                        ) : (
                          <img
                            src={DislikeIcon}
                            alt="dislike"
                            width={32}
                            height={32}
                          />
                        )}
                      </ActionButton>
                    </Tooltip>
                    <ShareButtonWrapper>
                      <ShareButton
                        id={id}
                        username={username}
                        shareType={ShareType.STORY}
                        copyLinkBoxPosition={'left'}
                        tooltipPosition={'left'}
                        isAutoHandleShareText={true}
                        templates={shareButtonTemplates}
                      />
                    </ShareButtonWrapper>
                  </>
                )}
                {!isPublished && (
                  <>
                    <WithEditMessage messageId={id}>
                      {({ handleOnClick, iconSource }) => (
                        <Tooltip
                          position="left"
                          renderHint={() => (
                            <TooltipText>
                              {t('edit', {
                                ns: TranslationNamespace.POST,
                              })}
                            </TooltipText>
                          )}
                          trigger="hover"
                        >
                          <ActionButton
                            data-element_id={ButtonId.All.ButtonEdit}
                            data-tracking_payload={{
                              'message.id': id,
                            }}
                            onClick={handleOnClick}
                          >
                            <img
                              src={iconSource}
                              alt={t('edit', {
                                ns: TranslationNamespace.POST,
                              })}
                              width={32}
                              height={32}
                            />
                          </ActionButton>
                        </Tooltip>
                      )}
                    </WithEditMessage>
                    <WithHideMessage messageId={id}>
                      {({ handleOnClick, iconSource }) => (
                        <Tooltip
                          position="left"
                          renderHint={() => (
                            <TooltipText>
                              {t('hide', {
                                ns: TranslationNamespace.POST,
                              })}
                            </TooltipText>
                          )}
                          trigger="hover"
                        >
                          <ActionButton
                            data-element_id={ButtonId.All.ButtonHiddenMassage}
                            data-tracking_payload={{
                              'message.id': id,
                            }}
                            onClick={handleOnClick}
                          >
                            <img
                              src={iconSource}
                              alt={t('hide', {
                                ns: TranslationNamespace.POST,
                              })}
                              width={32}
                              height={32}
                            />
                          </ActionButton>
                        </Tooltip>
                      )}
                    </WithHideMessage>
                  </>
                )}
                {this.getShouldShowMuteButton() && (
                  <MuteButton>
                    {({ isMuted, iconSource, handleOnClick }) => (
                      <Tooltip
                        position="left"
                        renderHint={() => (
                          <TooltipText>{isMuted ? 'Off' : 'On'}</TooltipText>
                        )}
                        trigger="hover"
                      >
                        <ActionButton
                          data-element_id={
                            isMuted
                              ? ButtonId.All.ButtonMuted
                              : ButtonId.All.ButtonVolume
                          }
                          data-tracking_payload={{
                            messageId: id,
                            isMuted: !isMuted,
                          }}
                          onClick={handleOnClick}
                        >
                          <ActionIcon
                            src={iconSource}
                            alt={isMuted ? 'audio off' : 'audio on'}
                          />
                        </ActionButton>
                      </Tooltip>
                    )}
                  </MuteButton>
                )}
              </ControlButtonsWrapper>
            )}
          </TopWrapper>
          <BottomWrapper
            isIphoneXSeriesPWA={getIsIphoneXSeriesPWA()}
            isInputFocused={isInputFocused}
            $hasAgeVerificationPinnedNotification={
              hasAgeVerificationPinnedNotification
            }
          >
            <MessagePanelBottom
              id={id}
              showInput={showInput && isUnlocked}
              showUnlockButton={showOperationsBar && !isUnlocked}
              showMeta={this.getShouldDisplayMeta()}
              footerNavigationHeight={
                getIsIphoneXSeriesPWA()
                  ? footerNavigationHeight + iPhoneXSeriesFooterPaddingInPWA
                  : footerNavigationHeight
              }
            />
          </BottomWrapper>
          {!isInputFocused && showNotifier && (
            <NotifierContainer isIphoneXSeriesPWA={getIsIphoneXSeriesPWA()}>
              {!isBroadcastListExpanded && (
                <NotifierWrapper>
                  {userId && <NewPostNotifier userId={userId} messageId={id} />}
                </NotifierWrapper>
              )}
              {isBroadcastListExpanded && userId && (
                <BroadcastList userId={userId} selectedMessageId={id} />
              )}
            </NotifierContainer>
          )}
          {showLoader && (
            <LoaderWrapper>
              <LogoLoading />
            </LoaderWrapper>
          )}
        </PanelCover>
      </StyledMessagePanel>
    );
  }

  initialize() {
    const { id, listType, setExpanded } = this.props;
    this.fetchData(id);

    if (listType === 'chat') {
      setExpanded({ expandedField: null, messageId: id });
    }
  }

  async fetchData(id) {
    const {
      fetchMeRating,
      fetchDetail,
      fetchViewScope,
      fetchUser,
      fetchOnlineStatus,
    } = this.props;

    await Promise.all([fetchDetail(id), fetchViewScope(id)]);

    if (this.props.userId) {
      fetchUser(this.props.userId);
      fetchOnlineStatus(this.props.userId);
    }

    if (this.props.isAuthed) {
      fetchMeRating(id);
    }
  }

  getShouldDisplayMeta = () => {
    const { listType, expandedField } = this.props;
    if (listType === 'story' || listType === 'messagePacks') {
      return true;
    }

    if (listType === 'chat') {
      return false;
    }

    return expandedField !== 'broadcastList';
  };

  getShouldShowNotifier = () => {
    const { outboxCount, expandedField, showNotifier, isPublished } =
      this.props;
    const expandedFlag =
      expandedField !== 'broadcastList' || expandedField !== 'input';

    return isPublished && showNotifier && expandedFlag && outboxCount > 0;
  };

  getShouldShowMuteButton = () => {
    const {
      isVolumeButtonEnabled,
      isFree,
      isUnlocked,
      mediaType,
      expandedField,
    } = this.props;
    const isVideo = 0 === mediaType.search('video');
    // Should hide mute button if user open related story list and input
    const shouldHideIfExpanded = ['broadcastList', 'input'].includes(
      expandedField
    );

    return (
      (isUnlocked || isFree ? true : isVolumeButtonEnabled) &&
      !shouldHideIfExpanded &&
      isVideo
    );
  };

  getStrunkHeight = () => {
    const {
      showInput,
      showOperationsBar,
      isUnlocked,
      isOnDesktop,
      isPublished,
    } = this.props;
    const showMeta = this.getShouldDisplayMeta();
    const showNotifier = this.getShouldShowNotifier();
    const showMessageInput = showInput && isUnlocked;
    const showUnlockButton = showOperationsBar && !isUnlocked;
    const showOperation = isPublished && (showMessageInput || showUnlockButton);
    const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();

    if (showMeta && showOperation) {
      return 116 + (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0);
    } else if (
      showNotifier ||
      showMeta ||
      showOperation ||
      (showOperation && showMeta)
    ) {
      return (
        (isOnDesktop ? 136 : 104) +
        (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0)
      );
    } else {
      return 68 + (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0);
    }
  };

  handleExpand = type => {
    const { id } = this.props;
    if (type) {
      this.props.setExpanded({ expandedField: type, messageId: id });
    } else {
      this.props.setExpanded({ expandedField: null, messageId: id });
    }
  };
}

MessagePanel.propTypes = {
  id: PropTypes.string,
  userId: PropTypes.string,
  username: PropTypes.string,
  mediaType: PropTypes.string,
  ratedAs: PropTypes.number,
  currentTime: PropTypes.number,
  duration: PropTypes.number,
  listType: PropTypes.string,
  outboxCount: PropTypes.number,
  expandedField: PropTypes.oneOf(['caption', 'broadcastList', 'input']),
  isVolumeButtonEnabled: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isPublished: PropTypes.bool,
  isUnlocked: PropTypes.bool,
  isFree: PropTypes.bool,
  isInputFocused: PropTypes.bool,
  hasAgeVerificationPinnedNotification: PropTypes.bool,
  isUserActive: PropTypes.bool,
  isOnDesktop: PropTypes.bool,
  showThumbnail: PropTypes.bool,
  showInput: PropTypes.bool,
  showOperationsBar: PropTypes.bool,
  showLoader: PropTypes.bool,
  showProgress: PropTypes.bool,
  showNotifier: PropTypes.bool,
  showMultiProgress: PropTypes.bool,
  totalCount: PropTypes.number,
  currentIndex: PropTypes.number,
  fetchMeRating: PropTypes.func,
  fetchDetail: PropTypes.func,
  fetchViewScope: PropTypes.func,
  fetchUser: PropTypes.func,
  fetchOnlineStatus: PropTypes.func,
  setExpanded: PropTypes.func,
  submitRating: PropTypes.func,
  shareButtonTemplates: PropTypes.array,
  t: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
};

MessagePanel.defaultProps = {
  id: '',
  userId: null,
  username: '',
  mediaType: '',
  ratedAs: undefined,
  currentTime: 0,
  duration: 0,
  listType: null,
  outboxCount: 0,
  expandedField: null,
  isVolumeButtonEnabled: false,
  isAuthed: false,
  isPublished: true,
  isUnlocked: false,
  isFree: false,
  isInputFocused: false,
  hasAgeVerificationPinnedNotification: false,
  isUserActive: false,
  isOnDesktop: false,
  showThumbnail: false,
  showInput: false,
  showOperationsBar: false,
  showLoader: false,
  showProgress: false,
  showNotifier: false,
  showMultiProgress: false,
  totalCount: 0,
  currentIndex: 0,
  fetchMeRating: () => null,
  fetchDetail: () => null,
  fetchViewScope: () => null,
  fetchUser: () => null,
  fetchOnlineStatus: () => null,
  setExpanded: () => null,
  submitRating: () => null,
  shareButtonTemplates: [],
  isActive: undefined,
};

const StyledMessagePanel = styled.div`
  position: relative;
  height: 100%;
  pointer-events: none;
`;

const ThumbnailWrapper = styled.div.attrs(({ shouldShowPoster }) => ({
  style: {
    opacity: shouldShowPoster ? 1 : 0,
    zIndex: shouldShowPoster ? 1 : 0,
  },
}))`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  font-size: 0px;
  transition: opacity 0.6s ease-out;
`;

const PanelCover = styled.div`
  position: relative;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  z-index: 3;

  background: linear-gradient(
    to bottom,
    ${({ isExpanded }) =>
      // Add 0.15 opacity when caption is expanded
      isExpanded
        ? `
          rgba(0, 0, 0, 0.65) 0%,
          rgba(0, 0, 0, 0.25) 20%,
          rgba(0, 0, 0, 0.19) 25%,
          rgba(0, 0, 0, 0.15) 33%,
          rgba(0, 0, 0, 0.65) 100%
          `
        : `
          rgba(0, 0, 0, 0.5) 0%,
          rgba(0, 0, 0, 0.1) 20%,
          rgba(0, 0, 0, 0.04) 25%,
          rgba(0, 0, 0, 0) 33%,
          rgba(0, 0, 0, 0.5) 100%
        `}
  );
`;

const ProgressWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 16px;
  left: 16px;
  pointer-events: auto;
`;

const TopWrapper = styled.div`
  position: relative;
  padding: 44px 16px 16px;
  height: 100%;
  background: none;
  max-height: ${({ shrinkHeight }) => `calc(100% - ${shrinkHeight}px)`};
  ${media.mobile`
    padding: 28px 16px 0;
  `}
`;

const MessagePanelTopWrapper = styled.div`
  height: 100%;
`;

const getIphoneXBottom = ({ isIphoneXSeriesPWA, isInputFocused }) =>
  isIphoneXSeriesPWA && !isInputFocused ? iPhoneXSeriesFooterPaddingInPWA : 0;

const BottomWrapper = styled.div`
  position: absolute;
  right: 0;
  left: 0;
  bottom: ${props =>
    props.$hasAgeVerificationPinnedNotification
      ? 40
      : getIphoneXBottom(props)}px;

  ${media.tablet`
    bottom: ${getIphoneXBottom}px;
  `}
`;

const NotifierContainer = styled.div`
  position: absolute;
  width: 100%;
  background: none;
  bottom: ${({ isIphoneXSeriesPWA }) =>
    80 + (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0)}px;
  ${media.mobile`
    bottom: ${({ isIphoneXSeriesPWA }) =>
      72 + (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0)}px
  `}
`;

const NotifierWrapper = styled.div`
  position: relative;
  float: right;
  right: 16px;
  pointer-events: auto;
`;

const LoaderWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ControlButtonsWrapper = styled.div`
  position: absolute;
  right: 16px;
  bottom: 0px;
  pointer-events: auto;
`;

const actionButtonStyle = css`
  ${ResetButtonStyle};
  margin-bottom: 16px;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.neutral['55_alpha_20']};
`;

const ShareButtonWrapper = styled.div`
  ${actionButtonStyle};
  ${TooltipStyleComponent} {
    transform: translateX(-4px);
  }
`;

const ActionButton = styled.button`
  ${actionButtonStyle};
`;

const ActionIcon = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`;

const TooltipText = styled.div`
  height: 30px;
  margin-left: -28px;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.5);
  background-color: rgba(246, 246, 246, 0.8);
  color: ${color.black};
  text-align: center;
  white-space: nowrap;
  font-size: 14px;
`;

export default withTranslation()(MessagePanel);
